import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
  type UseSuspenseQueryOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { getWfAuthnClient, setAuthCookies, useAxios, isWfAuthnMockEnabled } from "auth-sdk";
import { BaseSchemas } from "api-types";
import { isAxiosError } from "axios";
import type { AxiosError, AxiosResponse } from "axios";
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { StaffAppConfigSchemaOut } from "staff-v2-types";
import { CUSTOMER_UUID_QUERY_KEY } from "shared-utils";

export const STAFF_APP_CONFIG_QUERY_KEY = "staff-app-config";
export const CUSTOMER_APP_CONFIG_QUERY_KEY = "customer-app-config";

export const useStaffAppConfig = (): UseSuspenseQueryResult<StaffAppConfigSchemaOut> => {
  const headers = { "x-current-location": window.location.pathname };
  const axiosClient = useAxios();
  return useSuspenseQuery<AxiosResponse<StaffAppConfigSchemaOut>, AxiosError, StaffAppConfigSchemaOut>({
    queryKey: [STAFF_APP_CONFIG_QUERY_KEY],
    queryFn: async () => {
      if (!isWfAuthnMockEnabled()) {
        // HACK: Blast in access token cookies incase they became expired.
        // Since this gets called almost immediately and suspends everything else,
        // it's a decent place to inject them.
        const client = getWfAuthnClient();
        let accessToken = await client.getAccessToken();
        if (accessToken === undefined) {
          accessToken = await client.getAccessToken({ refresh: true });
          if (accessToken === undefined) {
            const next = window.location.pathname + window.location.search;
            window.location.href = "/authn/login-staff?next=" + encodeURIComponent(next);
            throw new Error("Can't get access token, redirecting to login");
          }
        }
        setAuthCookies({ accessToken: accessToken.jwt });
      }
      return axiosClient.get<StaffAppConfigSchemaOut>("/staff/v2/api/app-config", { headers });
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ data }) => data,
  });
};
export const useCustomerAppConfig = (
  options: Omit<
    UseSuspenseQueryOptions<AxiosResponse<BaseSchemas.CustomerAppConfig>, AxiosError, BaseSchemas.CustomerAppConfig>,
    "queryKey" | "queryFn" | "select"
  > = {}
): UseSuspenseQueryResult<BaseSchemas.CustomerAppConfig> => {
  const { customerDomain } = useParams();
  const queryClient = useQueryClient();
  const axiosClient = useAxios();
  const isAuthPath = location.pathname.includes("authn-wf");
  const path = customerDomain ? `/${customerDomain}/app-config` : "/app-config";
  return useSuspenseQuery<AxiosResponse<BaseSchemas.CustomerAppConfig>, AxiosError, BaseSchemas.CustomerAppConfig>({
    queryKey: [CUSTOMER_APP_CONFIG_QUERY_KEY, customerDomain ? customerDomain : "public"],
    queryFn: async () => {
      try {
        const response = await axiosClient.get<BaseSchemas.CustomerAppConfig>(path);

        // Cache the customerUuid for the duration of the session
        if (response.data.customerUuid) {
          queryClient.setQueryDefaults([CUSTOMER_UUID_QUERY_KEY], {
            staleTime: Infinity,
            gcTime: Infinity,
          });
          queryClient.setQueryData([CUSTOMER_UUID_QUERY_KEY], response.data.customerUuid);
        }

        return response;
      } catch (e: unknown) {
        // If we get a 404, it might be because the customer domain doesn't exist. In that case, we can fallback to the public app config.
        if (isAxiosError(e) && e.status === 404) {
          const response = await axiosClient.get<BaseSchemas.CustomerAppConfig>("/app-config");
          return response;
        }
        throw e;
      }
    },
    select: ({ data }) => data,
    refetchOnMount: !isAuthPath,
    staleTime: isAuthPath ? 60 * 1000 : 0,
    ...options,
  });
};
